// src/pages/VeryShortansedit.jsx
import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import "../styles/Shortans.css";
import { FaBell, FaUserCircle } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const LOCAL_SELECTED_KEY = "selectedQuestionsByChapter"; // unified key
const apiBase = "https://digital.tifleducation.com/admin/public/api";

const VeryShortansedit = () => {
  const navigate = useNavigate();
  const { paperId } = useParams();
  const [activeItem, setActiveItem] = useState("papers");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [user, setUser] = useState({ id: null, name: "", email: "", password: "" });
  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState({});
  const [veryShortLimit, setVeryShortLimit] = useState(0);

  // -------------------- FETCH DATA --------------------
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return toast.error("User not logged in.");

        // User info
        const dashRes = await axios.get(`${apiBase}/getDashboard`, { headers: { Authorization: `Bearer ${token}` } });
        if (dashRes.data?.user) setUser({ ...dashRes.data.user, password: "" });

        // Limit
        const regularForm = JSON.parse(localStorage.getItem("regularPaperForm") || "{}");
        setVeryShortLimit(Number(regularForm.veryshort || 0));

        // Board/Grade/Subject
        const boardId = localStorage.getItem("selectedBoardId");
        const gradeId = localStorage.getItem("selectedGradeId");
        const subjectId = localStorage.getItem("selectedSubjectId");
        if (!boardId || !gradeId || !subjectId) return toast.error("Missing board/grade/subject.");

        // Fetch questions
        let data = {};
        if (paperId && paperId !== "new") {
          const res = await axios.get(`${apiBase}/papers/${paperId}`, { headers: { Authorization: `Bearer ${token}` } });
          data = res.data?.questions || {};
        } else {
          const res = await axios.post(
            `${apiBase}/papers/fetch-questions`,
            { board: Number(boardId), grade: Number(gradeId), subject: Number(subjectId) },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          data = res.data || {};
        }

        const vsQs = [];
        Object.entries(data).forEach(([chapterId, chapterData]) => {
          const types = chapterData.types || {};
          const arr = types["Very Short Question Answer"] || [];
          (Array.isArray(arr) ? arr : []).forEach(q => vsQs.push({ ...q, chapter_id: q.chapter_id ?? Number(chapterId) }));
        });
        setQuestions(vsQs);

        // Restore selected
        const saved = JSON.parse(localStorage.getItem(LOCAL_SELECTED_KEY) || "{}");
        const selectedMap = {};
        Object.values(saved || {}).forEach(ch => {
          const arr = ch.very_short_qa || [];
          arr.forEach(id => selectedMap[Number(id)] = true);
        });
        setSelectedQuestions(selectedMap);

      } catch (error) {
        console.error("Error fetching very short questions:", error.response?.data || error);
        toast.error("Failed to fetch very short questions.");
      }
    };

    fetchData();
  }, [paperId]);

  // -------------------- SELECT QUESTION --------------------
  const handleSelectQuestion = async (qId, chapterId) => {
    const alreadySelected = !!selectedQuestions[qId];
    const currentCount = Object.keys(selectedQuestions).length;

    if (!alreadySelected && !veryShortLimit) return toast.error("No very short question limit set.");
    if (!alreadySelected && currentCount >= veryShortLimit) return toast.error(`You can only select up to ${veryShortLimit} questions.`);

    const nextSelected = { ...selectedQuestions };
    if (nextSelected[qId]) delete nextSelected[qId];
    else nextSelected[qId] = true;
    setSelectedQuestions(nextSelected);

    // Save in unified localStorage
    try {
      const saved = JSON.parse(localStorage.getItem(LOCAL_SELECTED_KEY) || "{}");
      const key = String(chapterId);
      if (!saved[key]) saved[key] = {
        mcq: [], fill_in_the_blanks: [], true_false: [],
        very_short_qa: [], short_qa: [], long_qa: [],
        matching_type: [], diagram_image_based: []
      };

      const arr = saved[key].very_short_qa;
      const idx = arr.findIndex(x => Number(x) === Number(qId));
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(Number(qId));

      saved[key].very_short_qa = arr;
      localStorage.setItem(LOCAL_SELECTED_KEY, JSON.stringify(saved));

      if (paperId && paperId !== "new") {
        const token = localStorage.getItem("token");
        await axios.put(`${apiBase}/papers/${paperId}`, { selectedQuestions: saved }, { headers: { Authorization: `Bearer ${token}` } });
      }

      toast.success("Selection saved");
    } catch (error) {
      console.error("Error saving very short selection:", error.response?.data || error);
      toast.error("Failed to save selection");
    }
  };

  // -------------------- SAVE PROFILE --------------------
  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${apiBase}/update-profile`, { name: user.name, password: user.password }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Profile updated successfully!");
      setIsModalOpen(false);
      setUser({ ...user, password: "" });
    } catch (error) {
      console.error("Error updating profile:", error.response?.data || error);
      toast.error("Failed to update profile");
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} activeItem={activeItem} setActiveItem={setActiveItem} />

      <main className="main-content">
        <div className="short-header">
          <div className="short-welcome-text">
            <a onClick={() => navigate(-1)} className="back-link"><i className="fa fa-arrow-left" /></a>
            <span>Very Short Questions</span>
          </div>
          <div className="header-icons"><FaBell className="icon" /><FaUserCircle className="icon" onClick={() => setIsModalOpen(true)} /></div>
        </div>

        <div className="short-questions-container">
          {questions.length ? questions.map((q, idx) => (
            <div key={q.id} className="short-question-box">
              <div className="short-question">
                <input type="checkbox" className="short-checkbox"
                  checked={!!selectedQuestions[q.id]}
                  onChange={() => handleSelectQuestion(q.id, q.chapter_id)}
                  disabled={!selectedQuestions[q.id] && Object.keys(selectedQuestions).length >= veryShortLimit}
                />
                <span className="question-text">{idx + 1}. {String(q.question || q.question_title || "").replace(/<\/?[^>]+(>|$)/g, "")}</span>
              </div>
            </div>
          )) : (
            <div className="no-questions-container">
              <div className="no-questions-icon">📄</div>
              <h3>No Very Short Questions Found</h3>
              <p style={{ color: "#666" }}>There are currently no very short questions for this Board / Grade / Subject.</p>
            </div>
          )}
        </div>
      </main>

      {/* Profile Modal */}
      {isModalOpen && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.modal}>
            <button style={modalStyles.closeBtn} onClick={() => setIsModalOpen(false)}>&times;</button>
            <div style={modalStyles.userIcon}><FaUserCircle /></div>
            <div style={modalStyles.formGroup}>
              <label>Username</label>
              <input type="text" value={user.name} onChange={e => setUser({ ...user, name: e.target.value })} style={modalStyles.input} />
            </div>
            <div style={modalStyles.formGroup}>
              <label>Email</label>
              <input type="email" value={user.email} disabled style={{ ...modalStyles.input, background: "#f0f0f0", color: "#888" }} />
            </div>
            <div style={modalStyles.formGroup}>
              <label>Password</label>
              <input type="password" value={user.password} onChange={e => setUser({ ...user, password: e.target.value })} style={modalStyles.input} />
            </div>
            <div style={{ textAlign: "right" }}>
              <button style={modalStyles.saveBtn} onClick={handleSaveProfile}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const modalStyles = {
  overlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
  modal: { background: "#fff", padding: "2rem", borderRadius: "12px", width: "90%", maxWidth: "400px", position: "relative", boxShadow: "0 4px 15px rgba(0,0,0,0.2)" },
  closeBtn: { position: "absolute", top: "10px", right: "15px", background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer" },
  userIcon: { textAlign: "center", fontSize: "4rem", color: "#129255", marginBottom: "1rem" },
  formGroup: { marginBottom: "1rem" },
  input: { width: "100%", padding: "0.5rem 0.8rem", borderRadius: "6px", border: "1px solid #ccc", outline: "none" },
  saveBtn: { background: "#129255", color: "#fff", border: "none", padding: "0.6rem 1.2rem", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }
};

export default VeryShortansedit;
